import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import NeonButton from '../shared/NeonButton';
import NordokLogo from '../shared/NordokLogo';
import PlaceholderImage from '../shared/PlaceholderImage';

const Card = styled(motion.div)`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.medium};
  transition: transform ${props => props.theme.transitions.medium}, 
              box-shadow ${props => props.theme.transitions.medium},
              filter ${props => props.theme.transitions.medium};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: scale(1.1, 1.1);
    box-shadow: ${props => props.theme.shadows.large};
  }
  
  p.tip {
    font-size: 1em;
    font-weight: 700;
  }
  
  p.second-text {
    font-size: 0.7em;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: 220px;
  background-color: ${props => props.theme.colors.lightGray};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, transparent 70%, rgba(0, 0, 0, 0.4) 100%);
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform ${props => props.theme.transitions.medium};
  
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CardContent = styled.div`
  padding: ${props => props.theme.spacing.md};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xlarge};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.darkGray};
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Tag = styled.span`
  background-color: ${props => props.theme.colors.lightGray};
  color: ${props => props.theme.colors.darkGray};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: 500;
`;

const ProjectDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  margin-bottom: ${props => props.theme.spacing.md};
  flex-grow: 1;
`;

const ExpandedContent = styled(motion.div)`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  
  .project-neon-button:hover {
    color: ${props => props.theme.colors.black || '#000000'};
  }
`;

const ProjectCard = ({ project }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Hover handlers
  const handleMouseEnter = () => {
    setExpanded(true);
  };
  
  const handleMouseLeave = () => {
    setExpanded(false);
  };
  
  // Check if this is a Nordok project
  const isNordokProject = project.title?.toLowerCase() === 'nordok';
  
  // Get image source based on whether it's a Nordok project
  const projectImage = isNordokProject 
    ? null 
    : (project.images && project.images.length > 0 
      ? project.images[0] 
      : null);
  
  return (
    <Card
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      layout
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageContainer>
        {isNordokProject ? (
          <NordokLogo width="80%" height="80%" />
        ) : projectImage ? (
          <ProjectImage 
            src={projectImage}
            alt={project.title} 
          />
        ) : (
          <PlaceholderImage text={project.title} />
        )}
      </ImageContainer>
      
      <CardContent>
        <ProjectTitle>{project.title}</ProjectTitle>
        
        <ProjectTags>
          {project.tags.map(tag => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </ProjectTags>
        
        <ProjectDescription className="tip">{project.description}</ProjectDescription>
        
        {expanded && (
          <ExpandedContent
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p>{project.fullDescription}</p>
          </ExpandedContent>
        )}
        
        <ButtonGroup>
          {expanded && project.url && (
            <NeonButton 
              as="a" 
              href={project.url} 
              target="_blank" 
              rel="noopener noreferrer"
              small
              className="project-neon-button"
            >
              Try Now
            </NeonButton>
          )}
        </ButtonGroup>
      </CardContent>
    </Card>
  );
};

export default ProjectCard; 